import {GraphQL} from "./GraphQL";
import {cuppa, log} from "../../cuppa/cuppa";
import {CuppaStorage} from "../../cuppa/cuppa.storage";
import {Storages} from "./Storages";
import {Globals} from "./Globals";
import {UserCtrl} from "./UserCtrl";
import {Constants} from "./Constants";

export class OrganizationCtrl {
	static async loadOrganization({id = null, activationCode = null, loadImages = true} = {}){
		if(!id && !activationCode) return;
		let query = `#graphql
			{
				organization(id:${id}, activationCode:"${activationCode || ''}"){
					id, name, description, activation_code, status, date, ps_matching_algorithm, delete_message_history, show_interactive_notes, push_notification_new_request, timezone_id, disable_active_cases_dashboard, escalation_by_inactivity
					${!loadImages ? `` : `logo`}
					requestTypes {
							id, organization_id, type, label, config, order
          }
				}
			}
		`;
		let result = await GraphQL.query({query, addRootBrackets:false, printResult:false});
		return result?.data?.organization;
	}

	static async loadUserOrganizations({userId, loadLogo = false, status = null, search = null, orderBy = "name", orderType = "ASC"} = {}){
		let filters = {};
			if(status) filters.status = status;
			if(search) filters.search = search;
			if(orderBy) filters.orderBy = orderBy;
			if(orderType) filters.orderType = orderType;
		let types = {status:'enum', orderType:'enum'};
		let query = `#graphql
    	{
          userOrganizations(userId:"${userId}", filters: ${cuppa.dataToGraphQLInput(filters, {types})}){
              id, name, activation_code, status, push_notification_new_request,
              ${loadLogo ? 'logo' : ''}
	          	organizationsLinked {
			          organization {
				          name
                }
              }
          }
	    }
		`;
		let result = await GraphQL.query({query, addRootBrackets:false});
		return result;
	}

	static async switchOrganization({id, reload = true}){
		let currentUser = await CuppaStorage.getData(Storages.user);
		let organization = await OrganizationCtrl.loadOrganization({id});
		let user = await UserCtrl.loadUser({id:currentUser.id, organizationId:organization.id});
		CuppaStorage.setData({...Storages.user, data:user?.data?.user}).then();
		CuppaStorage.setData({...Storages.organization, data:organization}).then();
		if(reload) document.location.href = "/";
	}

	static getOrganization(){
		return CuppaStorage.getDataSync(Storages.organization);
	}

	static newOrganization(){
		return {
			name:'',
			description:'',
			activation_code:'',
			status:Constants.organizationStatuses.ACTIVE,
			ps_matching_algorithm:Constants.organizationPSMatchingAlgorithm.DISABLED,
			delete_message_history:1,
			logo:'',
			show_interactive_notes:true,
			push_notification_new_request:true,
			requestTypes:[],
		}
	}

	static async setOrganization({organization}){
		const types = {
			id:'remove',
			description:'multiline',
			status:'enum',
			ps_matching_algorithm:'enum',
			delete_message_history:'number',
			show_interactive_notes:'boolean',
			push_notification_new_request:'boolean',
			disable_active_cases_dashboard:'boolean',
			requestTypes:{id:'number', type:'enum', order:'number'}
		};
		let result = await GraphQL.query({mutation:`#graphql
      mutation {
        setOrganization(id: "${organization?.id || null}", organization:${cuppa.dataToGraphQLInput(organization, {types})} ){
          id
        }
      }
		`, addRootBrackets:false, printQuery:false, printResult:false});
		return result;
	}

	static async isActivationCodeValid({activationCode, organizationId}){
		if(!activationCode) return false;
		const query = await GraphQL.query({query:`#graphql
        query {
            organization(activationCode: "${activationCode}"){
                id, activation_code, 
            }
        }
			`, addRootBrackets:false});
		const organization = query?.data?.organization;
		if(!organization) return true;
		if(organization?.id && organization?.id == organizationId) return true;
		return false;
	}

	static newOrganizationLink(){
		return {
			organization_id:OrganizationCtrl.getOrganization().id,
			link_id:0,
			order:0,
		}
	}

	static async addOrganizationLink({id, organizationLink}){
		let types = {order:'number', organization:'remove', id:'remove'};
		let mutation = `#graphql
	    mutation {
	      addOrganizationLink(id: ${id || null}, organizationLink: ${cuppa.dataToGraphQLInput(organizationLink, {types})}){
	        id
	      }
	    }
		`
		return await GraphQL.query({mutation, addRootBrackets:false});
	}

	static async removeOrganizationLink(organizationLinkId){
		if(!organizationLinkId) return;
		await GraphQL.query({mutation:`#graphql
      mutation {
        removeOrganizationLink(id: "${organizationLinkId}")
      }
			`, addRootBrackets:false})
		return true;
	}

	static newRequestType(){
		return {
			organization_id:0,
			type:Constants.organizationRequestTypes.TEXT_MESSAGE,
			label:'',
			config:null,
			order:0
		}
	}

	static newRequestTypeTypePhone(){
		return {phone: '', title: '', description: ''}
	}

	static newRequestTypeTypeBrowser(){
		return {url: '', title: '', description: ''}
	}

	static async getOrganizationLink({id}){
		if(!id) return null;
		return await GraphQL.query({query:`#graphql
      {
        organizationLink(id:"${id}" ){
	        id, organization_id,
	        organization {
		        id, name
          }
        }
      }
		`, addRootBrackets:false});
	}

	static newOrganizationLinkQuestion({organizationLinkId, questionId}){
		return {
			organization_link_id:organizationLinkId,
			question_id:questionId,
		}
	}

	static async addOrganizationLinkQuestion({organizationLinkQuestion} = {}){
		return await GraphQL.query({query:`#graphql
			mutation {
				addOrganizationLinkQuestion(organizationLinkQuestion: ${cuppa.dataToGraphQLInput(organizationLinkQuestion)}){
					id
				}
			}
		`, addRootBrackets:false, printResult:false, printQuery:false})
	}

	static async getOrganizationLinkQuestions({organizationLinkId} = {}){
		if(!organizationLinkId) return;
		return await GraphQL.query({query:`#graphql
      {
	      organizationLinkQuestions(organizationLinkId: "${organizationLinkId}"){
		      id, organization_link_id, question_id,
		      question {
			      id, question
          }
	      }
      }	
		`, addRootBrackets:false, printResult:false, printQuery:false})
	}

	static async removeOrganizationLinkQuestion({id} = {}){
		if(!id) return;
		return await GraphQL.query({query:`#graphql
      mutation {
        removeOrganizationLinkQuestion(id: "${id}")
      }
			`, addRootBrackets:false, printResult:false, printQuery:false})
	}

	static async createQuickSightViews({organizationId, timezoneOffset}){
		if(!organizationId || !timezoneOffset) return;
		return await GraphQL.query({query:`#graphql
			mutation {
        createQuickSightViews(organizationId: "${organizationId}", timezoneOffset: "${timezoneOffset}")
      }
    `, addRootBrackets:false, printQuery:false, printResult:false})
	}

}
